import { useState, useEffect } from 'react';
import { useDataProvider, useNotify, useAuthProvider, Title } from 'react-admin';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';

// buildListLink builds a react-admin list URL with a filter pre-applied.
// The filter must be URL-encoded JSON because react-admin reads it from the
// `filter` query parameter on mount.
const buildListLink = (resource: string, filter?: Record<string, unknown>) => {
  if (!filter || Object.keys(filter).length === 0) return `/${resource}`;
  return `/${resource}?filter=${encodeURIComponent(JSON.stringify(filter))}`;
};
import {
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Build as BuildIcon,
  Schedule as ScheduleIcon,
  Memory as MemoryIcon,
} from '@mui/icons-material';
import type { Metrics } from '../../types';
import type { LogtoDataProvider } from "../../dataProvider.ts";

interface MetricCardProps {
  title: string;
  value: number | string;
  unit?: string;
  icon?: React.ReactElement;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  subtitle?: string;
  linkTo?: string;
}

const MetricCard = ({
  title,
  value,
  unit,
  icon,
  color = 'primary',
  subtitle,
  linkTo,
}: MetricCardProps) => {
  const card = (
    <Card
      sx={{
        height: '100%',
        ...(linkTo && {
          cursor: 'pointer',
          transition: 'transform 0.15s, box-shadow 0.15s',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
        }),
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography color="text.secondary" gutterBottom variant="body2">
            {title}
          </Typography>
          {icon && (
            <Box sx={{ color: `${color}.main` }}>
              {icon}
            </Box>
          )}
        </Box>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography variant="h4" component="div" color={`${color}.main`}>
            {value}
          </Typography>
          {unit && (
            <Typography variant="body1" color="text.secondary">
              {unit}
            </Typography>
          )}
        </Box>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
  if (!linkTo) return card;
  return (
    <Link to={linkTo} style={{ textDecoration: 'none', color: 'inherit' }}>
      {card}
    </Link>
  );
};

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes === 0) {
    return `${remainingSeconds}s`;
  }
  return `${minutes}m ${remainingSeconds}s`;
};

const getSuccessRateColor = (rate: number): 'success' | 'warning' | 'error' => {
  if (rate >= 95) return 'success';
  if (rate >= 90) return 'warning';
  return 'error';
};

export const MetricsDashboard = () => {
  const dataProvider = useDataProvider<LogtoDataProvider>();
  const authProvider = useAuthProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<Metrics | null>(null);

  const fetchMetrics = () => {
    dataProvider
      .getMetrics()
      .then(({ data }) => {
        if (data) {
          setMetrics(data);
        }
      })
      .catch((error) => {
        // getMetrics is a custom dataProvider method, so react-admin does not
        // auto-route its errors through checkError. Forward manually: an expired
        // token (401) triggers re-auth/redirect, a genuine missing metrics:read
        // scope (403, valid token) falls through and shows "not available".
        authProvider?.checkError?.(error).catch(() => {});
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchMetrics();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchMetrics();
    }, 30000);

    return () => clearInterval(interval);
  }, [dataProvider, notify]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!metrics) {
    return (
      <Box p={4}>
        <Title title="Dashboard" />
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <MemoryIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Metrics are not available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You don't have permission to view metrics, or the data is not yet available.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const successRateColor = getSuccessRateColor(metrics.successRate);
  const clientActivePercentage = metrics.totalClients > 0
    ? ((metrics.activeClients / metrics.totalClients) * 100).toFixed(1)
    : '0.0';
  const completedPercentage = metrics.totalBuilds > 0
    ? ((metrics.completedBuilds / metrics.totalBuilds) * 100).toFixed(1)
    : '0.0';
  const failedPercentage = metrics.totalBuilds > 0
    ? ((metrics.failedBuilds / metrics.totalBuilds) * 100).toFixed(1)
    : '0.0';

  return (
    <Box p={2}>
      <Title title="System Metrics" />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          System Metrics Dashboard
        </Typography>
        <Chip
          label={`Last updated: ${new Date(metrics.timestamp).toLocaleTimeString()}`}
          size="small"
          variant="outlined"
        />
      </Box>

      {/* Section 1: Client Overview */}
      <Box mb={4}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Client Overview
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <MetricCard
            title="Total Clients"
            value={metrics.totalClients}
            icon={<PeopleIcon />}
            color="primary"
            linkTo={buildListLink('clients')}
          />
          <MetricCard
            title="Active Clients"
            value={metrics.activeClients}
            subtitle={`${clientActivePercentage}% of total`}
            icon={<CheckCircleIcon />}
            color="success"
            linkTo={buildListLink('clients', { status: 'active' })}
          />
          <MetricCard
            title="Pending Clients"
            value={metrics.pendingClients}
            icon={<ScheduleIcon />}
            color="warning"
            linkTo={buildListLink('clients', { status: 'pending' })}
          />
          <MetricCard
            title="Inactive Clients"
            value={metrics.inactiveClients}
            icon={<ErrorIcon />}
            color="error"
            linkTo={buildListLink('clients', { status: 'inactive' })}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Section 2: Build Overview */}
      <Box mb={4}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Build Overview
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <MetricCard
            title="Total Builds"
            value={metrics.totalBuilds}
            icon={<BuildIcon />}
            color="primary"
            linkTo={buildListLink('builds')}
          />
          <MetricCard
            title="Queued Builds"
            value={metrics.queuedBuilds}
            icon={<ScheduleIcon />}
            color={metrics.queuedBuilds > 20 ? 'warning' : 'info'}
            subtitle={metrics.queuedBuilds > 20 ? 'High queue depth' : undefined}
            linkTo={buildListLink('builds', { status: 'queued' })}
          />
          <MetricCard
            title="In Progress"
            value={metrics.inProgressBuilds}
            icon={<MemoryIcon />}
            color="info"
            linkTo={buildListLink('builds', { status: 'building' })}
          />
          <MetricCard
            title="Completed Builds"
            value={metrics.completedBuilds}
            subtitle={`${completedPercentage}% of total`}
            icon={<CheckCircleIcon />}
            color="success"
            linkTo={buildListLink('builds', { status: 'completed' })}
          />
          <MetricCard
            title="Failed Builds"
            value={metrics.failedBuilds}
            subtitle={`${failedPercentage}% of total`}
            icon={<ErrorIcon />}
            color="error"
            linkTo={buildListLink('builds', { status: 'failed' })}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Section 3: Success Rate */}
      <Box mb={4}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Success Rate
        </Typography>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" py={3}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                {successRateColor === 'success' && <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />}
                {successRateColor === 'warning' && <WarningIcon sx={{ fontSize: 48, color: 'warning.main' }} />}
                {successRateColor === 'error' && <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />}
                <Typography variant="h2" color={`${successRateColor}.main`}>
                  {metrics.successRate.toFixed(2)}%
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary">
                {successRateColor === 'success' && 'Excellent performance'}
                {successRateColor === 'warning' && 'Good performance, monitor failures'}
                {successRateColor === 'error' && 'Action required: High failure rate'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Section 4: Performance */}
      <Box mb={4}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Performance
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
          <MetricCard
            title="Average Build Duration"
            value={formatDuration(Math.round(metrics.avgBuildDurationSeconds))}
            icon={<ScheduleIcon />}
            color="info"
            subtitle="Mean time for all builds"
          />
          <MetricCard
            title="P95 Build Duration"
            value={formatDuration(Math.round(metrics.p95BuildDurationSeconds))}
            icon={<ScheduleIcon />}
            color="warning"
            subtitle="95th percentile"
          />
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Section 5: System Resources */}
      <Box mb={4}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          System Resources
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
          <MetricCard
            title="Active Workers"
            value={metrics.activeWorkers}
            unit="workers"
            icon={<MemoryIcon />}
            color="success"
            subtitle="Processing builds"
          />
          <MetricCard
            title="Queue Depth"
            value={metrics.queueDepth}
            unit="jobs"
            icon={<ScheduleIcon />}
            color={metrics.queueDepth > 50 ? 'error' : metrics.queueDepth > 20 ? 'warning' : 'success'}
            subtitle={metrics.queueDepth > 50 ? 'Critical: High queue' : metrics.queueDepth > 20 ? 'Warning: Growing queue' : 'Healthy'}
          />
        </Box>
      </Box>
    </Box>
  );
};
